import React, { useState, useEffect, useRef } from 'react';
import './App.css';

const App = () => {
  const [scrollY, setScrollY] = useState(0);
  const [visibleSections, setVisibleSections] = useState(new Set()); // All sections hidden initially
  const [leavingSections, setLeavingSections] = useState(new Set());
  const [typedText, setTypedText] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const linkedIn = "https://www.linkedin.com/in/zoi-bena-562211174/";
  const email = 'zoi.mpena@gmail.com';
  const phone = '+306988826984';
  // Tech skills array
  const techSkills = [
    'HTML',
    '(S)CSS',
    'Typescript',
    'React',
    'Angular',
    'Tailwind CSS',
    'Git',
    'Azure',
    'Docker',
    'Node.js',
    'Three.js',
    'GSAP',
    'Magnolia CMS',
    'Wordpress CMS',
    'Illustrator',
  ];

  const workExperience = [
    {
      title: 'Software Engineer',
      date: 'May 2023 - Present',
      description: 'Software Engineer mainly as a Frontend Developer',
      place: 'PRODYNA SE',
      animationDelay: '0.5s',
      slidePosition: 'left'
    },
    {
      title: 'Freelancer',
      date: 'Jan 2019 - Present',
      description: 'Freelance Developer & Designer',
      place: 'Website projects',
      animationDelay: '0.7s',
      slidePosition: 'right'
    },
    {
      title: 'Frontend Web Developer',
      date: 'Dec 2019 - May 2023',
      description: 'Angular Developer & Web Designer',
      place: 'Upgrade',
      animationDelay: '0.9s',
      slidePosition: 'left'
    },
    {
      title: 'Web Developer & Designer',
      date: 'Apr 2019 - 2021',
      description: 'Web Developer, UI Designer',
      place: 'CvBrain startup company',
      animationDelay: '1.1s',
      slidePosition: 'right'
    },
    {
      title: 'Internship',
      date: 'Nov 2018 - Mar 2019',
      description: 'UX Designer & Frontend Developer for E-learning platform',
      place: 'Attikon University General Hospital',
      animationDelay: '1.3s',
      slidePosition: 'left'
    },
  ];

  const educationData = [
    {
      type: 'degree',
      title: 'Bachelor\'s Degree in Computer Science',
      institution: 'Athens University of Economics and Business',
      date: '2012 - 2020',
      specializations: [
        'Information Systems and Information Security',
        'Operational Research and Economics of Information Technology'
      ],
      animationDelay: '0.5s',
      slidePosition: 'left'
    },
    {
      type: 'seminar',
      title: 'Magnolia CMS Training',
      institution: 'Professional Development',
      date: 'Oct 2023',
      description: 'PaaS Project Dev training',
      animationDelay: '0.7s',
      slidePosition: 'right'
    },
    {
      type: 'course',
      title: 'Illustrator CC',
      institution: 'Online Course',
      date: 'Jan 2023',
      description: 'UI & Web Design with Illustrator',
      animationDelay: '0.9s',
      slidePosition: 'left'
    },
    {
      type: 'course',
      title: 'Angular Full Course',
      institution: 'Online Course',
      date: 'Dec 2019',
      description: 'Advanced SCSS',
      animationDelay: '1.1s',
      slidePosition: 'right'
    },
    {
      type: 'course',
      title: 'Web Development Fundamentals',
      institution: 'Online Course',
      date: 'Dec 2018 - Feb 2019',
      description: 'HTML5, CSS3, JavaScript, Responsive web design',
      animationDelay: '1.3s',
      slidePosition: 'left'
    }
  ];

  const certifications = [
    {
      title: 'Certified Angular Developer',
      date: 'Nov 2024',
      category: 'Frontend Development'
    },
    {
      title: 'Professional Scrum Developer I (PSD I)',
      date: 'Sep 2024',
      category: 'Agile Methodology'
    },
    {
      title: 'Magnolia Trained & Certified Project Developer',
      date: 'Jan 2024',
      category: 'CMS Development'
    },
    {
      title: 'Magnolia Trained & Certified Headless Developer',
      date: 'Jan 2024',
      category: 'Headless CMS'
    },
    {
      title: 'Angular - The Complete Guide',
      date: '2020',
      category: 'Frontend Development'
    }
  ];

  // Refs for each section
  const heroRef = useRef();
  const aboutRef = useRef();
  const workRef = useRef();
  const educationRef = useRef();
  const certificationsRef = useRef();

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Typing effect for the title
  useEffect(() => {
    const fullText = "Hi, I'm Zoi Bena, a Software Engineer based in Athens, Greece.";
    let currentIndex = 0;

    const typeText = () => {
      if (currentIndex <= fullText.length) {
        setTypedText(fullText.substring(0, currentIndex));
        currentIndex++;
        setTimeout(typeText, 100); // Adjust typing speed here (milliseconds)
      }
    };

    // Start typing after a short delay
    const startDelay = setTimeout(typeText, 500);

    return () => {
      clearTimeout(startDelay);
    };
  }, []);

  // Intersection Observer for sliding animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.2,
      rootMargin: '0px 0px 0px 0px'
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        const sectionId = entry.target.id;

        if (entry.isIntersecting) {
          // Section is entering
          setLeavingSections(prev => {
            const newSet = new Set(prev);
            newSet.delete(sectionId);
            return newSet;
          });

          setTimeout(() => {
            setVisibleSections(prev => new Set([...prev, sectionId]));
          }, 100);

        } else {
          // Section is leaving
          setVisibleSections(prev => {
            const newSet = new Set(prev);
            newSet.delete(sectionId);
            return newSet;
          });

          setTimeout(() => {
            setLeavingSections(prev => new Set([...prev, sectionId]));
          }, 100);

          // Remove from leaving state after animation completes
          setTimeout(() => {
            setLeavingSections(prev => {
              const newSet = new Set(prev);
              newSet.delete(sectionId);
              return newSet;
            });
          }, 900);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    const sections = [heroRef.current, aboutRef.current, workRef.current, educationRef.current, certificationsRef.current];
    sections.forEach(section => {
      if (section) observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (sectionIndex) => {
    const sections = [heroRef.current, aboutRef.current, workRef.current, educationRef.current, certificationsRef.current];
    const targetSection = sections[sectionIndex];

    if (targetSection) {
      targetSection.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
      // Close mobile menu after navigation
      setMobileMenuOpen(false);
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Function to render text with special styling for "Zoi Bena"
  const renderTypedText = (text) => {
    const fullText = "Hi, I'm Zoi Bena, a Software Engineer based in Athens, Greece.";
    const nameStartIndex = fullText.indexOf("Zoi Bena");
    const nameEndIndex = nameStartIndex + 8; // "Zoi Bena" is 8 characters

    // Check if we've started typing the name
    if (text.length >= nameStartIndex + 1) {
      const beforeName = text.substring(0, nameStartIndex);

      // Get how much of the name has been typed
      const namePartLength = Math.min(text.length - nameStartIndex, 8);
      const namePart = text.substring(nameStartIndex, nameStartIndex + namePartLength);

      // Get text after the name (if any)
      const afterName = text.length > nameEndIndex ? text.substring(nameEndIndex) : '';

      const showCursor = text.length < fullText.length;

      // Split at "based in" for desktop line break
      const basedInIndex = afterName.indexOf(' based in');
      const beforeBased = basedInIndex !== -1 ? afterName.substring(0, basedInIndex) : afterName;
      const afterBased = basedInIndex !== -1 ? afterName.substring(basedInIndex) : '';

      return (
        <span className="typed-text block font-normal leading-tight">
          {beforeName}
          <span className="title inline font-extrabold">{namePart}</span>
          {beforeBased}
          {afterBased && <br className="hidden md:block" />}
          {afterBased}
          {showCursor && <span className="typing-cursor">|</span>}
        </span>
      );
    }

    // Before we reach the name, just show regular text
    return (
      <span className="typed-text font-normal leading-tight">
        {text}
        <span className="typing-cursor">|</span>
      </span>
    );
  };

  return (
    <div>
      {/* Header */}
      <header className="fixed top-0 left-0 w-full shadow-sm z-50 header-footer-bg border-b border-gray-500/10">
        <div className="container mx-auto px-12 flex justify-between items-center">
          <img
            src={`${process.env.PUBLIC_URL}/logo.png`}
            alt="Logo"
            className="h-20 w-auto"
          />

          {/* Mobile Menu Button */}
          <button
            className={`mobile-menu-button w-10 h-10 flex md:hidden flex-col gap-1.5 p-2 border-0 cursor-pointer ${mobileMenuOpen ? 'open' : ''}`}
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            <span className="line line1 w-7 h-0.5 rounded-sm"></span>
            <span className="line line2 w-7 h-0.5 rounded-sm"></span>
            <span className="line line3 w-7 h-0.5 rounded-sm"></span>
          </button>

          {/* Desktop Navigation */}
          <nav className="desktop-nav hidden md:flex">
            <ul className="flex space-x-4">
              <li><button onClick={() => scrollToSection(0)} className="links">Intro</button></li>
              <li><button onClick={() => scrollToSection(1)} className="links">About</button></li>
              <li><button onClick={() => scrollToSection(2)} className="links">Work Experience</button></li>
              <li><button onClick={() => scrollToSection(3)} className="links">Education</button></li>
              <li><button onClick={() => scrollToSection(4)} className="links">Certifications</button></li>
            </ul>
          </nav>
        </div>

        {/* Mobile Navigation */}
        <nav className={`mobile-nav fixed w-full border-b border-gray-500/10 ${mobileMenuOpen ? 'open' : 'hidden'}`}>
          <ul className="py-4 flex flex-col">
            <li className="p-0"><button className="w-full py-4 px-8" onClick={() => scrollToSection(0)}>Intro</button></li>
            <li className="p-0"><button className="w-full py-4 px-8" onClick={() => scrollToSection(1)}>About</button></li>
            <li className="p-0"><button className="w-full py-4 px-8" onClick={() => scrollToSection(2)}>Work Experience</button></li>
            <li className="p-0"><button className="w-full py-4 px-8" onClick={() => scrollToSection(3)}>Education</button></li>
            <li className="p-0"><button className="w-full py-4 px-8" onClick={() => scrollToSection(4)}>Certifications</button></li>
          </ul>
        </nav>
      </header>

      {/* Background Image Layer */}
      <div
        className="parallax-bg fixed w-full h-screen"
        style={{ transform: `translateY(${scrollY * 0.1}px)`, background: '#E4DDD7' }}
      >
      </div>

      {/* Content Sections */}
      <div className="parallax-content relative z-1">
        {/* Intro Section - Hero */}
        <section
          ref={heroRef}
          id="hero"
          className={`parallax-section hero-section w-full h-screen flex items-center justify-center py-8 md:py-12 ${visibleSections.has('hero') ? 'section-entering section-visible' :
            leavingSections.has('hero') ? 'section-leaving' : ''
            }`}
          style={{ transform: `translateY(${scrollY * -0.3}px)` }}
        >
          <div className="hero-content relative text-center z-10 w-11/12 md:w-4/5 mx-auto">
            <div className="hero-text-container">
              <div className="hero-title m-0 leading-[0.9]">
                <div className="hero-name slide-left" style={{ animationDelay: '0.1s' }}>
                  <div className="typing-container relative overflow-hidden block py-8 px-4 md:py-12 md:px-8 text-3xl md:text-5xl lg:text-5xl">
                    {renderTypedText(typedText)}
                    <div className="aurora">
                      <div className="aurora__item"></div>
                      <div className="aurora__item"></div>
                      <div className="aurora__item"></div>
                      <div className="aurora__item"></div>
                    </div>
                  </div>
                </div>
              </div>
              {/* <div className="hero-details slide-right text-base md:text-xl flex flex-col gap-2 font-light">
                <p className="hero-location flex items-center justify-center gap-2">
                  <span className="material-icons-outlined">location_on</span>
                  Athens, Greece
                </p>
                <p className="flex items-center justify-center gap-2">
                  <span className="material-icons-outlined">email</span>
                  <a className='links' target='_blank' href={`mailto:${email}`}>{email}</a>
                </p>
                <p className="flex items-center justify-center gap-2">
                  <span className="material-icons-outlined">phone</span>
                  <a className='links' target='_blank' href={`tel:${phone}`}>{phone}</a>
                </p>
                <p className="flex items-center justify-center">
                  <a
                    href={linkedIn}
                    target="_blank"
                    rel="noopener noreferrer"
                    className='w-100 flex gap-2 items-center'
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                    </svg>
                  <span className='links'>
                    LinkedIn
                  </span>
                  </a>
                </p>
              </div> */}
              <div className="hero-cta slide-left flex justify-center" style={{ animationDelay: '0.6s' }}>
                <button
                  className="resume-button font-semibold gap-2 relative overflow-hidden items-center justify-center py-3 px-5 text-sm md:text-base border-0 rounded-full cursor-pointer"
                  onClick={() => window.open(`${process.env.PUBLIC_URL}/ZoiBenaCV.pdf`, '_blank')}
                >
                  <span className="material-icons-outlined">description</span>
                  My Resume
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section
          ref={aboutRef}
          id="about"
          className={`parallax-section about-section w-full min-h-screen flex items-center justify-center py-8 md:py-12 ${visibleSections.has('about') ? 'section-entering section-visible' :
            leavingSections.has('about') ? 'section-leaving' : ''
            }`}
          style={{ transform: `translateY(${scrollY * -0.2}px)` }}
        >
          <div className="section-content light-section w-11/12 md:w-4/5 mx-auto p-6 sm:py-8 sm:px-6 md:p-12 lg:p-16 border border-white/40 rounded-3xl">
            <div className="content-container relative">
              <div className="section-number -top-4 md:-top-8 lg:-top-16 -left-4 md:-left-8 slide-left text-5xl md:text-7xl lg:text-9xl absolute font-black" style={{ animationDelay: '0.1s' }}>01</div>
              <h2 className="section-title slide-right mb-8 text-4xl md:text-5xl lg:text-6xl font-extrabold" style={{ animationDelay: '0.3s' }}>
                About Me
              </h2>
              <div className="content-text">
                <p className="large-text slide-left mb-6 text-xl md:text-2xl font-light" style={{ animationDelay: '0.5s' }}>
                  Software Engineer & Design Enthusiast with a passion for creating intuitive, high-performance web applications.
                </p>
                <p className="regular-text slide-right max-w-xl text-sm md:text-xl" style={{ animationDelay: '0.7s' }}>
                  With expertise in React, Angular, and cloud technologies, I transform complex requirements into elegant solutions. 
                  I focus on clean code, responsive design, and performance optimization to create exceptional digital experiences.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Work Experience Section */}
        <section
          ref={workRef}
          id="work"
          className={`parallax-section work-section w-full min-h-screen flex items-center justify-center py-8 md:py-12 ${visibleSections.has('work') ? 'section-entering section-visible' :
            leavingSections.has('work') ? 'section-leaving' : ''
            }`}
          style={{ transform: `translateY(${scrollY * -0.15}px)` }}
        >
          <div className="section-content light-section w-11/12 md:w-4/5 mx-auto p-6 sm:py-8 sm:px-6 md:p-12 lg:p-16 border border-white/40 rounded-3xl">
            <div className="content-container relative">
              <div className="section-number -top-4 md:-top-8 lg:-top-16 -left-4 md:-left-8 slide-right text-5xl md:text-7xl lg:text-9xl absolute font-black" style={{ animationDelay: '0.1s' }}>02</div>
              <h2 className="section-title slide-left mb-8 text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.1]" style={{ animationDelay: '0.3s' }}>
                Work Experience
              </h2>
              <div className="work-grid mt-0 md:mt-12 grid gap-4 md:gap-8">
                {workExperience.map((work, index) => (
                  <div className={`work-item p-5 sm:p-6 md:p-5 lg:p-8 slide-${work.slidePosition} border border-white/15 border-l-4 rounded-xl`} style={{ animationDelay: work.animationDelay }}>
                    <div className='flex justify-between items-center mb-2'>
                      <h3 className="work-title text-base md:text-xl font-semibold">{work.title}</h3>
                      <p className="text-sm">{work.date}</p>

                    </div>
                    <p className="work-description italic mb-3 text-sm md:text-base leading-relaxed">{work.description}</p>
                    <p className="work-description justify-self-end text-sm md:text-base leading-relaxed">{work.place}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Education Section */}
        <section
          ref={educationRef}
          id="education"
          className={`parallax-section education-section w-full min-h-screen flex items-center justify-center py-8 md:py-12 ${visibleSections.has('education') ? 'section-entering section-visible' :
            leavingSections.has('education') ? 'section-leaving' : ''
            }`}
          style={{ transform: `translateY(${scrollY * -0.1}px)` }}
        >
          <div className="section-content light-section w-11/12 md:w-4/5 mx-auto p-6 sm:py-8 sm:px-6 md:p-12 lg:p-16 border border-white/40 rounded-3xl">
            <div className="content-container relative">
              <div className="section-number -top-4 md:-top-8 lg:-top-16 -left-4 md:-left-8 slide-left text-5xl md:text-7xl lg:text-9xl absolute font-black" style={{ animationDelay: '0.1s' }}>03</div>
              <h2 className="section-title slide-right mb-8 text-4xl md:text-5xl lg:text-6xl font-extrabold" style={{ animationDelay: '0.3s' }}>
                Education & Seminars
              </h2>
              <div className="education-grid mt-0 md:mt-12 grid gap-6 md:gap-8">
                {educationData.map((education, index) => (
                  <div key={index} className={`education-item p-5 sm:p-6 md:p-5 lg:p-8 ${education.type}-item slide-${education.slidePosition} border border-white/15 border-l-4 rounded-xl`} style={{ animationDelay: education.animationDelay }}>
                    <div className="education-header mb-4 flex flex-col items-start gap-2 md:flex-row md:justify-between md:items-center md:gap-0">
                      <span className="education-type px-3 py-1 text-xs md:text-sm font-bold border border-white/20 rounded-xl">{education.type}</span>
                      <span className="education-date text-xs md:text-sm font-medium">{education.date}</span>
                    </div>
                    <h3 className="education-title mb-2 text-base md:text-xl lg:text-2xl font-bold">{education.title}</h3>
                    <p className="education-institution mb-4 text-sm md:text-lg font-semibold">{education.institution}</p>
                    {education.description && (
                      <p className="education-description text-sm md:text-base leading-relaxed">{education.description}</p>
                    )}
                    {education.specializations && (
                      <div className="specializations mt-4 border-t border-white/10">
                        <p className="specializations-label mb-2 text-sm md:text-base font-semibold">Specializations:</p>
                        <ul className="specializations-list p-0 m-0 flex flex-col gap-1">
                          {education.specializations.map((spec, specIndex) => (
                            <li key={specIndex} className="specialization-item pl-4 text-sm md:text-base flex gap-2">{spec}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Certifications & Skills Section */}
        <section
          ref={certificationsRef}
          id="certifications"
          className={`parallax-section certifications-section w-full min-h-screen flex items-center justify-center py-8 md:py-12 ${visibleSections.has('certifications') ? 'section-entering section-visible' :
            leavingSections.has('certifications') ? 'section-leaving' : ''
            }`}
          style={{ transform: `translateY(${scrollY * -0.05}px)` }}
        >
          <div className="section-content light-section w-11/12 md:w-4/5 mx-auto p-6 sm:py-8 sm:px-6 md:p-12 lg:p-16 border border-white/40 rounded-3xl">
            <div className="content-container relative">
              <div className="section-number -top-4 md:-top-8 lg:-top-16 -left-4 md:-left-8 slide-right text-5xl md:text-7xl lg:text-9xl absolute font-black" style={{ animationDelay: '0.1s' }}>04</div>
              <h2 className="section-title slide-left mb-8 text-4xl md:text-5xl lg:text-6xl font-extrabold" style={{ animationDelay: '0.3s' }}>
                Certifications & Skillset
              </h2>
              <div className="certifications-skills-container mt-0 md:mt-12 grid gap-10 md:gap-16">
                <div className="certifications-section-left slide-left" style={{ animationDelay: '0.5s' }}>
                  <h3 className="section-subtitle mb-8 text-xl md:text-2xl lg:text-3xl font-bold">Professional Certifications</h3>
                  <div className="certifications-list flex flex-col gap-4">
                    {certifications.map((cert, index) => (
                      <div key={index} className="certification-item p-5 lg:p-6 border border-white/15 border-l-4 rounded-2xl">
                        <div className="certification-header mb-2 flex flex-col items-start gap-4 md:flex-row md:justify-between md:items-start">
                          <h4 className="certification-title m-0 text-base md:text-lg font-semibold leading-[1.3]">{cert.title}</h4>
                          <span className="text-sm font-medium">{cert.date}</span>
                        </div>
                        <span className="certification-category text-xs md:text-sm font-medium">{cert.category}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="skills-section-right slide-right" style={{ animationDelay: '0.7s' }}>
                  <h3 className="section-subtitle mb-8 text-xl md:text-2xl lg:text-3xl font-bold">Technical Skills</h3>
                  <div className="tech-tags flex flex-wrap gap-2">
                    {techSkills.map((skill, index) => (
                      <span key={index} className="tech-tag py-2 px-5 text-sm md:text-base font-medium border border-white/15 rounded-full">{skill}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Footer */}
      <footer className="site-footer left-0 w-full shadow-sm z-50 header-footer-bg pt-6 px-4 pb-4 sm:pt-8 sm:px-6 md:pt-12 md:px-8 md:pb-6 mt-16">
        <div className="footer-content mx-auto mb-8 w-11/12 md:w-4/5 flex justify-between items-start gap-6 md:gap-12">
          <div className="footer-section footer-contact flex flex-col gap-2">
            <h3 className="pb-2 mb-4 text-base md:text-xl font-semibold">Get In Touch</h3>
            <p className="text-sm md:text-base flex items-center gap-2">
              <span className="material-icons-outlined">location_on</span>
              Athens, Greece
            </p>
            <p className="text-sm md:text-base flex items-center gap-2">
              <span className="material-icons-outlined">email</span>
              <a className='links' target='_blank' href={`mailto:${email}`}>{email}</a>
            </p>
            <p className="text-sm md:text-base flex items-center gap-2">
              <span className="material-icons-outlined">phone</span>
              <a className='links' target='_blank' href={`tel:${phone}`}>{phone}</a>

            </p>
          </div>

          <div className="footer-section footer-social flex flex-col gap-4">
            <h3 className="pb-2 text-base md:text-xl font-semibold">Connect</h3>
            <p className="flex text-sm md:text-base">
              <a
                href={linkedIn}
                target="_blank"
                rel="noopener noreferrer"
                className='w-100 flex gap-2 items-center'
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                </svg>
                <span className='links'>
                    LinkedIn
                  </span>
              </a>
            </p>
            <button
              className="footer-resume-btn w-fit py-3 px-5 text-sm md:text-base inline-flex items-center justify-center gap-2 font-semibold border-0 rounded-full cursor-pointer"
              onClick={() => window.open(`${process.env.PUBLIC_URL}/ZoiBenaCV.pdf`, '_blank')}
            >
              <span className="material-icons-outlined">description</span>
              My Resume
            </button>
          </div>
        </div>

        <div className="footer-bottom">
          <p className="m-0 text-sm md:text-base">&copy; {new Date().getFullYear()} Zoi Bena. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default App;
